import type { QueryClient } from '@tanstack/react-query';

/**
 * localStorage key used by the async-storage persister configured in `router.tsx`.
 * Kept in sync with the explicit `key` passed to `createAsyncStoragePersister`.
 */
export const PERSISTED_QUERY_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';

/**
 * Drop all cached query data from BOTH the in-memory client and the persisted
 * localStorage blob.
 *
 * `queryClient.clear()` only updates memory; the persister writes to localStorage
 * on a ~1s throttle, so we remove the key directly to guarantee a stale copy isn't
 * restored on the next load. Call this on auth transitions (login / logout / NGO
 * switch) so a session never inherits the previous state's cache — otherwise a
 * persisted `ngo.*` query result cached under the previous session could be restored
 * and read synchronously by the next NGO/user, showing stale (or someone else's) data
 * before the cookie-backed refetch can land.
 */
export function clearPersistedQueryCache(queryClient: QueryClient): void {
	queryClient.clear();
	if (typeof window !== 'undefined') {
		window.localStorage.removeItem(PERSISTED_QUERY_CACHE_KEY);
	}
}

/**
 * Run `restore` only once THIS document's own dehydrated payload has been applied to the query cache.
 *
 * WHY THE TIMING IS THE WHOLE FIX (TEC-3758). The persisted cache and the SSR payload are two sources for
 * the same queries, and react-query already resolves that correctly: `hydrate` overwrites an entry only
 * when the incoming data is NEWER, so a snapshot from a previous visit can never clobber the figures this
 * request just rendered. What it cannot protect is the FIRST client render. Restoring while the router was
 * built put the previous visit's figures in the cache before that render, so `useSuspenseQuery` resolved
 * from them instead of suspending on the streamed promise, the render disagreed with the text the server
 * had already written, and React reported `#418 args[]=text` and regenerated the campaign card subtree.
 * The payload then won, correctly, but a frame too late.
 *
 * Deferring closes that window without giving up the cache: afterwards every query the document carried
 * already holds something newer, so the restore is a no-op for them, while queries the document did NOT
 * carry — a campaign detail page seen last week, a reload on a flaky connection — are still served from
 * localStorage, which is the case the cache exists for.
 *
 * `DOMContentLoaded` is the signal because the payload travels in inline `<script>` tags at the end of
 * `<body>`: inline scripts block the parser, so by the time it fires they have all executed. A caller
 * arriving after parsing (a client-side navigation building a fresh router) runs immediately.
 *
 * Restoring is deliberately NOT split from the save subscription. `persistQueryClient` restores and only
 * then subscribes, and that order matters: a save triggered before the restore would dehydrate the current
 * document over the blob and destroy the very entries we were about to read.
 */
export function afterSsrPayloadApplied(restore: () => void): void {
	if (typeof document === 'undefined') return;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => restore(), { once: true });
		return;
	}

	restore();
}

/**
 * The live-counter queries, which must never reach `localStorage`.
 *
 * Their payload is a replay of one five-minute window: valuable for those five minutes and worthless
 * afterwards, since the counter asks for the current window on every load anyway. Persisting them is
 * what produced TEC-3807/TEC-3810 — a returning visitor restored a hundred-odd window entries, every
 * one of them stale, and TanStack Query refetched them together into a single 16 KB tRPC GET that the
 * service answered with a 500.
 *
 * The window key no longer travels in the query key (see `lib/live-window.ts`), so a fresh cache can no
 * longer grow that backlog. This predicate stays for two reasons: a blob already on a visitor's disk
 * still holds the old entries, and a persisted replay would be restored MINUTES out of date and shown
 * before the refetch lands.
 *
 * Matched by procedure path rather than by a prefix over `portal.*`, so no ordinary portal query loses
 * its warm start. `demo-recent-donations` is the internal counter's client-side fake: it never reaches
 * the network, but it accumulates in the same blob.
 */
export function isLiveDonationsQueryKey(queryKey: readonly unknown[]): boolean {
	const first = queryKey[0];
	if (first === 'demo-recent-donations') return true;
	if (!Array.isArray(first)) return false;
	return first[0] === 'portal' && (first[1] === 'getRecentDonations' || first[1] === 'getEmergencyDonations');
}

/**
 * Which queries are written to `localStorage`. Extracted from `router.tsx` so it can be unit-tested:
 * inline in the persister options it was unreachable, and the rule it encodes is exactly the kind that
 * breaks silently — a query that should not be persisted simply is, and nothing fails until a visitor
 * comes back days later.
 *
 * Cookies are the source of truth for auth. Persisting auth queries would make the previous user's
 * identity survive a page reload until the background refetch lands.
 */
export function shouldDehydrateQuery(query: {
	queryKey: readonly unknown[];
	state: { status: string; data?: unknown };
}): boolean {
	const first = query.queryKey[0];
	if (Array.isArray(first) && first[0] === 'auth') return false;
	if (isLiveDonationsQueryKey(query.queryKey)) return false;
	if (query.state.status !== 'success') return false;
	return true;
}
